import { API } from 'app/routes';
import axios from 'axios';
import { Action, action, computed, Computed, Thunk, thunk } from 'easy-peasy';
import { IssueOrServiceType } from '~/app/data/statuses/chat';
import { StoreModel } from '~/store';
import { CreateWorkOrderPayload, CreateWorkOrderResponse, FilterWorkOrderPayload, WorkOrderDepartment, WorkOrderDetail, WorkOrderDetailResponse, WorkOrderExcavatorTaskListResponse, WorkOrderFilterOptions, WorkOrderGeneralSettings, WorkOrderHistoryPayload, WorkOrderHistoryPayloadType, WorkOrderHistoryResponse, WorkOrderStatusesList, WorkOrderTaskDetail, WorkOrderTaskDetailPayload, WorkOrderTaskDetailResponse, WorkOrderTaskStatus } from '~/workorder/types';
import { Machine } from '~/types';

export interface WorkOrderModel {
    fetching: boolean
    setFetching: Action<WorkOrderModel, boolean>
    data: WorkOrderDetail
    setData: Action<WorkOrderModel, WorkOrderDetail>
    taskData: WorkOrderTaskDetail
    setTaskData: Action<WorkOrderModel, WorkOrderTaskDetail>
    getWorkOrderDetail: Thunk<WorkOrderModel, number, any, StoreModel, Promise<void>>
    getWorkOrderTaskDetail: Thunk<WorkOrderModel, WorkOrderTaskDetailPayload, any, StoreModel, Promise<void>>
    getWorkOrderHistory: Thunk<WorkOrderModel, WorkOrderHistoryPayload, any, StoreModel, Promise<WorkOrderHistoryResponse>>
    filters: FilterWorkOrderPayload
    setFilters: Action<WorkOrderModel, FilterWorkOrderPayload>
    machineFilters: FilterWorkOrderPayload
    setMachineFilters: Action<WorkOrderModel, FilterWorkOrderPayload>
    filterOptions: WorkOrderFilterOptions
    setFilterOptions: Action<WorkOrderModel, WorkOrderFilterOptions>
    taskCount: Computed<WorkOrderModel, {total: number; done: number;}>
    fetchExcavatorTaskList: Thunk<WorkOrderModel, Machine['id'], any, StoreModel, Promise<WorkOrderExcavatorTaskListResponse>>
    workOrderGeneralSettings: WorkOrderGeneralSettings
    setWorkOrderGeneralSettings: Action<WorkOrderModel, WorkOrderGeneralSettings>
    createWorkOrder: Thunk<WorkOrderModel, CreateWorkOrderPayload, any, StoreModel, Promise<CreateWorkOrderResponse>>
    departments: WorkOrderDepartment[]
    setDepartments: Action<WorkOrderModel, WorkOrderDepartment[]>
    getWorkorderDepartments: Thunk<WorkOrderModel, void, any, StoreModel, Promise<void>>
}

export const workOrderModel: WorkOrderModel = {
    fetching: false,
    setFetching: action((state, payload) => {
        state.fetching = payload;
    }),
    data: {
        id: 0,
        history: [],
        workOrderNo: '',
        status: 0,
        statusInfo: {
            comment: null,
            statusAt: null,
            statusBy: null
        },
        isStarted: false,
        lastUpdated: {
            date: null,
            name: null
        },
        excavator: {
            id: 0,
            name: '',
            intern: '',
            isOperatingTimeVisible: 0,
            operatingTimeAllowed: 0,
            isOperatingCountVisible: 0,
            operatingCountAllowed: 0
        },
        operatingTime: null,
        operatingCount: null,
        createdBy: {
            id: 0,
            name: ''
        },
        approvedBy: {
            id: 0,
            name: ''
        },
        personnel: {
            id: 0,
            name: ''
        },
        mechanics: [],
        workType: 0,
        repairLocation: '',
        costCategoryId: null,
        departmentId: null,
        chargedDepartmentId: null,
        customerId: null,
        projectId: null,
        sortingType: null,
        invoiceAcceptanceUser: null,
        scheduledStartDate: '',
        scheduledEndDate: '',
        actualStartDate: null,
        actualEndDate: null,
        description: '',
        comment: '',
        attachments: [],
        tasks: [],
        summary: {
            totalHours: 0,
            totalMaterials: 0
        },
        parts: [],
        materials: []
    },
    setData: action((state, payload) => {
        state.data = payload;
    }),
    getWorkOrderDetail: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.get<null, WorkOrderDetailResponse>(`${API.workOrder}/${payload}`)
            .then(data => {
                actions.setData(data.data);
            })
            .finally(() => actions.setFetching(false));
    }),
    getWorkOrderTaskDetail: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, WorkOrderTaskDetailResponse>(`${API.workOrder}/${payload.id}/tasks/${payload.taskId}`, { type: payload.type })
            .then(data => {
                actions.setTaskData(data.data);
            })
            .finally(() => actions.setFetching(false));
    }),
    getWorkOrderHistory: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, WorkOrderHistoryResponse>(`${API.workOrder}/history`, payload)
            .then(data => data)
            .finally(() => actions.setFetching(false));
    }),
    filters: {
        searchText: "",
        type: WorkOrderHistoryPayloadType.ALL,
        departments: [],
        status: []
    },
    setFilters: action((state, payload) => {
        state.filters = payload;
    }),
    machineFilters: {
        searchText: "",
        type: WorkOrderHistoryPayloadType.ALL,
        departments: [],
        status: []
    },
    setMachineFilters: action((state, payload) => {
        state.machineFilters = payload;
    }),
    filterOptions: {
        status: WorkOrderStatusesList.map(status => ({ id: status.toString(), name: status.toString() }))
    },
    setFilterOptions: action((state, payload) => {
        state.filterOptions = payload;
    }),
    taskData: {
        id: 0,
        title: '',
        type: IssueOrServiceType.ISSUE,
        status: WorkOrderTaskStatus.COMPLETED,
        description: '',
        users: [],
        parts: [],
        materials: [],
        attachments: []
    },
    setTaskData: action((state, payload) => {
        state.taskData = payload;
    }),
    taskCount: computed((state) => {
        const tasks = state.data.tasks ?? [];
        const totalCount = tasks.length;
        const doneCount = tasks.filter(task => task.status === WorkOrderTaskStatus.COMPLETED || task.status === WorkOrderTaskStatus.REMOVED).length;
        return { total: totalCount, done: doneCount };
    }),
    fetchExcavatorTaskList: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.get<null, WorkOrderExcavatorTaskListResponse>(`${API.workOrder}/excavator-task-list/${payload}`)
            .then(data => data)
            .finally(() => actions.setFetching(false));
    }),
    workOrderGeneralSettings: {
        hoursCount: 0,
        plannedStartDate: '',
        plannedEndDate: ''
    },
    setWorkOrderGeneralSettings: action((state, payload) => {
        state.workOrderGeneralSettings = payload ?? {};
    }),
    createWorkOrder: thunk((actions, payload) => {
        actions.setFetching(true);
        return axios.post<null, CreateWorkOrderResponse>(`${API.workOrder}/create`, payload)
            .then(data => data)
            .finally(() => actions.setFetching(false));
    }),
    departments: [],
    setDepartments: action((state, payload) => {
        state.departments = payload;
    }),
    getWorkorderDepartments: thunk((actions) => {
        actions.setFetching(true);
        return axios.get<null, { data: WorkOrderDepartment[] }>(`${API.workOrder}/departments`)
            .then(data => {
                actions.setDepartments(data.data);
            })
            .finally(() => actions.setFetching(false));
    })
};
